/**
 * World Clock — one planetary rotation, a local time for every TILE (M3 S9).
 *
 * The raid map already had a wall-clock day/night cycle, but it was a GLOBAL
 * one: every region shared a single `dayPhase`, so raiding the far east and the
 * far west showed the same sun. This promotes that cycle into a world clock.
 * There is still exactly one planet and one rotation — what changes is that
 * local time is derived from where you are standing on it.
 *
 * THE CLOCK IS KEYED TO TILES, NOT REGIONS. `getTilePhase({row, col})` is the
 * primitive; a region's clock is just its capital tile's, and a raid's clock is
 * just its site tile's. This costs nothing today (each region is one contiguous
 * blob, so its capital is a fair stand-in) and it is what stops the clock ever
 * having to be rewritten: the day a region is scattered across two hemispheres
 * — L6's world reseed, or simply a second Shard Cities on the far side — every
 * caller already asks about a place rather than about a name, and nothing here
 * has to change. Time and territory are separate facts about a tile.
 *
 * LONGITUDE COMES FROM THE MAP, NOT FROM A TABLE. A tile's longitude is its
 * COLUMN in `worldMap.js`, so geography and time cannot drift apart: move a
 * capital east and its clock moves with it. That is deliberate — see the header
 * of `js/data/worldMap.js`, which names this file as the reason it is
 * hand-authored and fixed. (L6's reseed inherits the coupling for free.)
 *
 * The planet rotates WEST TO EAST, so an eastern tile meets the sun first and
 * its local time runs AHEAD. `planetPhase()` is the time at the prime meridian
 * (the map's western edge); every local time is that plus a longitude offset.
 * The map is 17 columns wide and spans the whole planet, so the two outermost
 * capitals sit ~18 hours apart — the difference is meant to be dramatic.
 *
 * NO MECHANICAL EFFECT IN M3. Nothing here is read by a raid roll, a payout or
 * a check; it is time-of-day made visible while L3 calibrates. The full
 * day/night raid mechanic is L4 work, and it will read `getTilePhase` rather
 * than inventing a second clock.
 *
 * No browser dependencies.
 *
 * See docs/layers/3-syndicate/design.md §8.
 */

import * as config from '../constants.js';

// 1 game day = 10 real minutes. Lifted out of raidMapAnim.js, which owned it
// when the cycle was a rendering detail rather than a world fact.
export const DAY_MS = 600_000;

// Where "now" sits in the cycle on first ship. Arbitrary — the world runs
// continuously either way — but it is part of the clock's definition, so it
// lives with the clock.
const PHASE_OFFSET = 0.35;

// Sunrise/sunset are where the engine's own daylight curve LEAVES ITS FLOOR,
// not numbers picked to look right — see `dayLight` below. That curve is
// `0.4 + 0.85·cos(…)` clamped up to 0.4, so it sits on the floor for exactly
// as long as the cosine is negative: phase < 0.25 or >= 0.75. Twelve hours of
// light, 06:00 to 18:00.
//
// (roomImageGen's comment claims fifteen. That describes the UNCLAMPED curve;
// the clamp is what the pixels actually show, and it is also what decides when
// the renderer draws shadows at all — `light > 0.4` in drawRoomTile. Naming a
// dawn the renderer does not draw would put the word and the picture in
// disagreement, which is the one thing this file exists to prevent.)
export const SUNRISE = 0.25;
export const SUNSET = 0.75;

// How long dawn and dusk last, as a fraction of the day. 0.0625 = 1.5 hours,
// which is wide enough that a player who looks twice in a raid can catch one.
export const TWILIGHT = 0.0625;

/**
 * Time at the prime meridian, 0..1 (0 = midnight, 0.5 = noon).
 *
 * `now` is injectable so tests and the simulator are not at the mercy of the
 * wall clock — every function in this file takes it the same way.
 */
export function planetPhase(now = Date.now()) {
    return ((now / DAY_MS) + PHASE_OFFSET) % 1;
}

/**
 * Longitude of a map column as a fraction east of the prime meridian, 0..1.
 *
 * Tile CENTRES, not edges: a column is a band of longitude and the clock wants
 * a point in it. Without the half-tile the western border column would sit
 * exactly on the meridian and the eastern one a full tile short of wrapping.
 */
export function columnLongitude(col) {
    const width = config.worldMap.width;
    return ((col + 0.5) / width) % 1;
}

/**
 * Longitude of a TILE.
 *
 * Takes the whole `{row, col}` rather than a bare column, because every caller
 * holds a place and a place is a tile — and because the day a raid site or a
 * region has to answer for its LATITUDE (season, day length), the signature
 * already carries it. Row is genuinely unused today, and saying so here is
 * cheaper than an argument nobody can explain in a year.
 */
export function tileLongitude(tile) {
    return tile ? columnLongitude(tile.col) : null;
}

/** The capital tile of a region — where its clock is read. Null if unknown. */
export function capitalTile(regionId) {
    return config.worldMap.capitals[regionId] || null;
}

/** Longitude of a region, i.e. of its capital tile. Null for unknown ids. */
export function regionLongitude(regionId) {
    return tileLongitude(capitalTile(regionId));
}

// Terrain a raid can be sited on. Ocean is excluded because a raid happens on
// the ground, and `#` because it is the map's "nothing here" marker — the same
// land-only rule Operations already applies to staging points.
const RAID_TERRAIN = new Set(['.', '^']);

/**
 * Every tile a region owns, optionally narrowed to ground a raid can sit on.
 *
 * Reads `regionRows` directly rather than borrowing `operations-core`'s
 * `tileRegion()`: it is the same three lines against the same data, and the
 * world clock pulling in the whole Operations module (gameState, syndicate,
 * demand, blackout) to avoid them would be the worse trade by a distance.
 */
export function regionTiles(regionId, { raidable = false } = {}) {
    const { width, height, regionRows, terrainRows, regionOrder } = config.worldMap;
    const tiles = [];
    for (let row = 0; row < height; row++) {
        for (let col = 0; col < width; col++) {
            if (regionOrder[regionRows[row]?.[col]] !== regionId) continue;
            if (raidable && !RAID_TERRAIN.has(terrainRows[row]?.[col])) continue;
            tiles.push({ row, col });
        }
    }
    return tiles;
}

/**
 * Choose where in a region a raid actually happens.
 *
 * Called ONCE, when the grid is generated, and stored on the raid — so the site
 * survives a reload and a raid never teleports between paints. A region spans
 * three or four columns, which is four to six hours of longitude: two raids in
 * the same region can genuinely be at different times of day, and that is the
 * point of siting them rather than reading the capital's clock.
 *
 * Falls back to the capital if a region somehow owns no raidable ground — a
 * region with no site would otherwise silently read as the prime meridian,
 * which is a wrong answer rather than a missing one.
 */
export function pickRaidSite(regionId, rng = Math.random) {
    const tiles = regionTiles(regionId, { raidable: true });
    if (!tiles.length) return capitalTile(regionId);
    return tiles[Math.floor(rng() * tiles.length)];
}

/** Local time at a longitude. East is ahead — the planet turns west to east. */
export function localPhase(longitude, now = Date.now()) {
    return (planetPhase(now) + longitude) % 1;
}

/**
 * The engine's daylight curve, 0.4 (night floor) to 1 (noon).
 *
 * This is `roomImageGen`'s `rawLight` before its smoothstep, and it is the
 * shared definition of "how light is it" for everything that is not the tile
 * renderer itself: the day-clock gradient, the world-map shading and the
 * dawn/dusk boundaries above.
 */
export function dayLight(phase) {
    return Math.max(0.4, Math.min(1, 0.4 + 0.85 * Math.cos((phase - 0.5) * 2 * Math.PI)));
}

/** Daylight renormalised to 0 (fully dark) .. 1 (noon), for shading maths. */
export function daylightFraction(phase) {
    return (dayLight(phase) - 0.4) / 0.6;
}

/**
 * Which of the four named phases a local time falls in.
 *
 * Dawn and dusk are the twilight BANDS just inside sunrise and sunset, so the
 * bulk of a lit day reads as "day" rather than as an endless dawn.
 */
export function phaseName(phase) {
    const p = ((phase % 1) + 1) % 1;
    if (p < SUNRISE || p >= SUNSET) return 'night';
    if (p < SUNRISE + TWILIGHT) return 'dawn';
    if (p >= SUNSET - TWILIGHT) return 'dusk';
    return 'day';
}

export const PHASE_ICON = { night: '🌙', dawn: '🌅', day: '☀️', dusk: '🌇' };

/** Local time as HH:MM on a 24-hour clock. */
export function formatPhaseTime(phase) {
    const p = ((phase % 1) + 1) % 1;
    const totalMinutes = Math.floor(p * 24 * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * THE PRIMITIVE: the whole local time-of-day at a tile, computed once.
 *
 * Everything else in this file is a way of naming a tile. Falls back to the
 * prime meridian for a null tile rather than returning null — every caller is a
 * RENDERER, and a missing clock should degrade to the old global behaviour, not
 * blank the UI.
 */
export function getTilePhase(tile, now = Date.now()) {
    const longitude = tileLongitude(tile) ?? 0;
    const phase = localPhase(longitude, now);
    const name = phaseName(phase);
    return {
        tile: tile || null,
        longitude,
        phase,
        name,
        icon: PHASE_ICON[name],
        light: dayLight(phase),
        time: formatPhaseTime(phase),
    };
}

/**
 * A region's clock — read at its capital.
 *
 * Not "the time in the region": a region three columns wide spans four hours,
 * so there is no such single time. The capital is a real place that the whole
 * game already treats as the region's point (it is where convoys arrive), which
 * makes it the honest thing to put a clock on.
 */
export function getRegionPhase(regionId, now = Date.now()) {
    return { ...getTilePhase(capitalTile(regionId), now), regionId };
}

/**
 * A raid's clock — read at its SITE, falling back to the region's capital.
 *
 * The fallback is not decoration: saves written before raids were sited carry
 * no `raidSite`, and a raid in progress across that upgrade must keep showing a
 * plausible sky rather than snapping to the meridian.
 */
export function getRaidSitePhase(regionId, site, now = Date.now()) {
    return getTilePhase(site || capitalTile(regionId), now);
}

/** Every region's capital clock at one instant, west to east. */
export function allRegionPhases(now = Date.now()) {
    return Object.values(config.worldMap.regionOrder).map(id => getRegionPhase(id, now));
}
